import React, { useState, useCallback, useMemo } from 'react'
import { Modal, Button, Footer, Content } from 'rsuite'
import AppLoader from '@components/common/AppLoader'

interface ConfirmModalProps {
  show: boolean
  onHide: () => void
  onConfirm: () => void
  title: string | any
  content: string | any
  loading?: boolean
  confirmLabel?: string
  cancelLabel?: string
  confirmDisabled?: boolean
  toggleConfirmDisabled?: (disabled: boolean) => void
}

export function ConfirmModal ({
  show,
  onHide,
  onConfirm,
  content,
  title,
  loading,
  confirmLabel = 'Подтвердить',
  cancelLabel = 'Отмена',
  confirmDisabled = false,
  toggleConfirmDisabled
}: ConfirmModalProps) {
  return (
    <Modal size="xs" open={show} onClose={onHide}>
      {loading && <AppLoader size="md" content="Загрузка" />}

      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="relative overflow-hidden">
        <Content>
          {content
            ? React.cloneElement(<>{content}</>, {
              confirmDisabled,
              toggleConfirmDisabled
            })
            : null}
        </Content>
      </Modal.Body>
      <Modal.Footer>
        <Footer className="relative">
          <Button
            color="blue"
            disabled={confirmDisabled}
            appearance="primary"
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
          <Button appearance="subtle" onClick={onHide}>
            {cancelLabel}
          </Button>
        </Footer>
      </Modal.Footer>
    </Modal>
  )
}

export interface ConfirmModalHookResult {
  confirm: (title?: string, content?: string | any) => void
  confirmModalProps: ConfirmModalProps
}

interface ConfirmModalCallbacksSet {
  close: () => void
  stopLoading: () => void
  toggleConfirmDisabled: (disabled: boolean) => void
}

export interface ConfirmModalArgs {
  title?: string
  content?: string | any
  onConfirm?: (args: ConfirmModalCallbacksSet) => void
  onOpen?: (args: ConfirmModalCallbacksSet) => void
}
interface ConfirmModalState {
  show: boolean
  title?: string
  content?: string | any
  loading: boolean
  confirmDisabled: boolean
}

export function useConfirmModal ({
  content,
  title,
  onConfirm,
  onOpen
}: ConfirmModalArgs) {
  const [ state, setState ] = useState<ConfirmModalState>({
    show: false,
    title,
    content,
    loading: false,
    confirmDisabled: false
  })

  const handleHideModal = useCallback(() => {
    setState((state) => ({ ...state, show: false, loading: false }))
  }, [])

  const callbacks = useMemo(
    () => ({
      close: handleHideModal,
      stopLoading: () => setState((state) => ({ ...state, loading: false })),
      toggleConfirmDisabled: (confirmDisabled = false) =>
        setState((state) => ({ ...state, confirmDisabled }))
    }),
    [ handleHideModal ]
  )

  const handleConfirmClick = useCallback(() => {
    setState((state) => ({ ...state, loading: true }))
    onConfirm?.(callbacks)
  }, [ callbacks, onConfirm ])

  return useMemo<ConfirmModalHookResult>(
    () => ({
      confirm (title?: string, content?: string) {
        setState((state) => ({
          ...state,
          title: title ?? state.title,
          content: content ?? state.content,
          show: true
        }))

        onOpen?.(callbacks)
      },
      confirmModalProps: {
        show: state.show,
        onHide: handleHideModal,
        onConfirm: handleConfirmClick,
        content: state.content,
        title: state.title,
        loading: state.loading,
        confirmDisabled: state.confirmDisabled,
        toggleConfirmDisabled: callbacks.toggleConfirmDisabled
      }
    }),
    [
      callbacks,
      handleConfirmClick,
      handleHideModal,
      onOpen,
      state.confirmDisabled,
      state.content,
      state.loading,
      state.show,
      state.title
    ]
  )
}
